import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { connect, iAmReady } from '@tiendanube/nexo';
import { Box, Spinner, Text, Button, Card, Title } from '@nimbus-ds/components';
import nexo from './nexoClient';
import Home from './pages/Home';
import Settings from './pages/Settings';
import Webhooks from './pages/Webhooks';
import NexoSyncRoute from './components/NexoSyncRoute';

type StorageAccessState = 'checking' | 'needs-request' | 'granted' | 'denied' | 'not-needed';

const App: React.FC = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [storageAccessState, setStorageAccessState] = useState<StorageAccessState>('checking');

  // Connect to Nexo FIRST - this must happen quickly
  useEffect(() => {
    connect(nexo)
      .then(() => {
        setIsConnected(true);
        iAmReady(nexo);
      })
      .catch((err: Error) => {
        console.error('Nexo connection failed:', err);
        setConnectionError('No se pudo conectar con TiendaNube');
        // In development, allow continuing without Nexo
        if (import.meta.env.DEV) {
          setIsConnected(true);
        }
      });
  }, []);

  // Check storage access AFTER Nexo connection
  useEffect(() => {
    if (!isConnected) return;

    const checkStorageAccess = async () => {
      // Check if we're in a cross-origin iframe
      let isInIframe = false;
      try {
        isInIframe = window.self !== window.top;
      } catch {
        isInIframe = true;
      }

      if (!isInIframe) {
        console.log('Not in iframe, storage access not needed');
        setStorageAccessState('not-needed');
        return;
      }

      // Check if Storage Access API is supported
      if (!document.hasStorageAccess || !document.requestStorageAccess) {
        console.log('Storage Access API not supported');
        setStorageAccessState('not-needed');
        return;
      }

      try {
        const hasAccess = await document.hasStorageAccess();
        if (hasAccess) {
          console.log('Storage access already granted');
          setStorageAccessState('granted');
        } else {
          console.log('Storage access needed, will request on user interaction');
          setStorageAccessState('needs-request');
        }
      } catch (error) {
        console.error('Error checking storage access:', error);
        setStorageAccessState('not-needed');
      }
    };

    checkStorageAccess();
  }, [isConnected]);

  const handleRequestStorageAccess = async () => {
    try {
      await document.requestStorageAccess();
      console.log('Storage access granted by user');
      setStorageAccessState('granted');
    } catch (error) {
      console.error('Storage access denied:', error);
      setStorageAccessState('denied');
    }
  };

  const handleContinueWithoutAccess = () => {
    setStorageAccessState('denied');
  };

  // Show loading state while connecting to Nexo
  if (!isConnected && !connectionError) {
    return (
      <Box
        height="100vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Spinner size="large" />
      </Box>
    );
  }

  // Show storage access request UI (after Nexo is connected)
  if (storageAccessState === 'needs-request') {
    return (
      <BrowserRouter>
        <NexoSyncRoute>
          <Box
            height="100vh"
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding="4"
          >
            <Card>
              <Card.Body>
                <Box display="flex" flexDirection="column" gap="4" alignItems="center" padding="4">
                  <Title as="h2">Habilitar acceso completo</Title>
                  <Text textAlign="center">
                    Para que n8n funcione correctamente dentro de TiendaNube, necesitamos
                    habilitar el acceso a cookies de sesión.
                  </Text>
                  <Text fontSize="caption" color="neutral-textLow" textAlign="center">
                    Esto es necesario debido a las políticas de privacidad de los navegadores modernos.
                  </Text>
                  <Box display="flex" gap="2" flexWrap="wrap" justifyContent="center">
                    <Button appearance="primary" onClick={handleRequestStorageAccess}>
                      Habilitar acceso
                    </Button>
                    <Button appearance="transparent" onClick={handleContinueWithoutAccess}>
                      Continuar sin acceso (limitado)
                    </Button>
                  </Box>
                </Box>
              </Card.Body>
            </Card>
          </Box>
        </NexoSyncRoute>
      </BrowserRouter>
    );
  }

  // Show loading while checking storage access
  if (storageAccessState === 'checking') {
    return (
      <BrowserRouter>
        <NexoSyncRoute>
          <Box
            height="100vh"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Spinner size="large" />
          </Box>
        </NexoSyncRoute>
      </BrowserRouter>
    );
  }

  // Main app routes
  return (
    <BrowserRouter>
      <NexoSyncRoute>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/webhooks" element={<Webhooks />} />
        </Routes>
      </NexoSyncRoute>
    </BrowserRouter>
  );
};

export default App;
